import { debounce } from 'lodash';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import decodeLocation from '../../Lib/Utils/Helpers/decode-location';

import Page from '../Page/index.ts';
import Header from '../Header/index.ts';
import { FilterSearch } from '../FilterSearch/FilterSearch.tsx';
import InviteTokensDataGrid from './InviteTokensDataGrid.tsx';
import { Actions, hasPermissionTo } from '../../Lib/Constants/roles.ts';

const filterMap = {
  all: {},
  emailSent: {
    status: { value: 'EMAIL_SENT' },
  },
  tokenRegistered: {
    status: { value: 'TOKEN_REGISTERED' },
  },
  expired: {
    status: { value: 'TOKEN_EXPIRED' },
  },
};

const filterItems = [
  {
    icon: 'file/file-text',
    value: 'all',
    label: 'All',
  },
  {
    icon: 'envelope/envelope-arrow-up',
    value: 'emailSent',
    label: 'Email Sent',
  },
  {
    icon: 'pencil-edit/pencil-edit-swoosh',
    value: 'tokenRegistered',
    label: 'Token Registered',
  },
  {
    icon: 'clock/clock-off',
    value: 'expired',
    label: 'Expired',
  },
];

export default function InviteTokens(props) {
  const [params, setParams] = useState({
    ...decodeLocation(props.location.search),
    search: '',
    page: 1,
    sortBy: 'expires_at',
    sortOrder: 'DESC',
    filters: 'all',
    invitedBy: '',
  });
  const [tokens, setTokens] = useState({ count: 0, rows: [] });
  const [isFetching, setIsFetching] = useState(false);
  const { team } = props;

  useEffect(() => {
    if (!team.length) {
      props.getTeam();
    }
  }, [team.length]);

  useEffect(() => {
    setIsFetching(true);
    props
      .getTokens({
        ...params,
        invitedBy: undefined,
        filters: {
          ...filterMap[params.filters],
          vendor_type: { value: 'INFLUENCER' },
          requesting_party_id: params.invitedBy
            ? {
                value: params.invitedBy,
              }
            : undefined,
        },
      })
      .then((results) => {
        setTokens(results);
      })
      .catch(console.log)
      .finally(() => setIsFetching(false));
  }, [params]);

  return (
    <Page.Container>
      <Header.Major icon="token" title="Influencer Tokens" />
      <Header.Minor
        filter={{
          value: params.filters,
          onChange: (value) =>
            setParams((p) => ({
              ...p,
              filters: value,
            })),
          items: filterItems,
        }}
        search={{
          value: params.search,
          onChange: debounce(
            (value) => setParams((p) => ({ ...p, search: value })),
            500,
          ),
        }}
      >
        <FilterSearch
          withSearch
          value={params.invitedBy}
          items={[
            { value: '', label: 'Any' },
            ...props.team.map((item) => ({
              value: item.id,
              label: `${item.first_name} ${item.last_name}`,
            })),
          ]}
          label="Invited By"
          emptyLabel="Invited By"
          onAddValue={(value) =>
            setParams((p) => ({
              ...p,
              invitedBy: value,
            }))
          }
          onRemoveValue={() =>
            setParams((p) => ({
              ...p,
              invitedBy: '',
            }))
          }
        />
      </Header.Minor>
      <Page.Content>
        <InviteTokensDataGrid
          hasPermissionToRefresh={hasPermissionTo(
            'social-media-console/invite-tokens',
            props.currentUser.role,
            Actions.UPDATE,
            'REFRESH',
          )}
          rows={tokens.rows}
          count={tokens.count}
          team={props.team}
          loading={isFetching}
          refresh={props.refresh}
          params={params}
          sortModel={
            params?.sortBy
              ? [
                  {
                    field: params.sortBy,
                    sort: params.sortOrder === 'ASC' ? 'asc' : 'desc',
                  },
                ]
              : []
          }
          onSortModelChange={(model) => {
            const sortItem = model[0];
            setParams((p) => ({
              ...p,
              sortBy: sortItem?.field || undefined,
              sortOrder: sortItem?.sort.toUpperCase() || undefined,
            }));
          }}
          pageSizeOptions={[15]}
          paginationModel={{
            page: params?.page ? params.page - 1 : 0,
            pageSize: 15,
          }}
          onPaginationModelChange={(model) =>
            setParams((p) => ({ ...p, page: model.page + 1 }))
          }
        />
      </Page.Content>
    </Page.Container>
  );
}

InviteTokens.propTypes = {
  currentUser: PropTypes.shape({}),
  getTeam: PropTypes.func.isRequired,
  getTokens: PropTypes.func.isRequired,
  team: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  refresh: PropTypes.func.isRequired,
};
